/* ============================================
   SUPERBAND brand-features.js
   Broadway/Motion premium features for Dawn
   1. Scroll animations (IntersectionObserver)
   2. Video hero background
   3. Sticky add-to-cart bar
   4. Mega menu
   ============================================ */

(function () {
  'use strict';

  /* ==========================================
     1. SCROLL ANIMATIONS
     Add data-animate="fade-up|fade-in|slide-left|slide-right|scale-up"
     to any section or element in the Shopify Customizer
     (Additional HTML attributes field, or via theme.liquid)

     For staggered children: add data-animate-group to parent
     ========================================== */

  function initScrollAnimations() {
    const elements = document.querySelectorAll('[data-animate], [data-animate-group]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Once revealed, stop observing for performance
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ==========================================
     2. VIDEO HERO BACKGROUND
     Dawn's banner section doesn't support video natively.
     This injects a video behind any banner section that has
     a data-video-src attribute set on the section wrapper.

     In Customizer → Banner section → Additional HTML attributes:
     data-video-src="https://cdn.shopify.com/s/files/1/xxxx/yourfile.mp4"

     The section will automatically get hero--video class applied.
     ========================================== */

  function initVideoHero() {
    const banners = document.querySelectorAll('.banner[data-video-src]');

    banners.forEach((banner) => {
      const src = banner.getAttribute('data-video-src');
      if (!src) return;

      banner.classList.add('hero--video');

      const wrapper = document.createElement('div');
      wrapper.className = 'hero__video-bg';

      const video = document.createElement('video');
      video.src = src;
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('aria-hidden', 'true');

      wrapper.appendChild(video);
      banner.insertBefore(wrapper, banner.firstChild);

      // Respect reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        video.pause();
        // Show poster frame only
        const poster = banner.getAttribute('data-video-poster');
        if (poster) {
          wrapper.style.backgroundImage = `url(${poster})`;
          wrapper.style.backgroundSize = 'cover';
          wrapper.style.backgroundPosition = 'center';
          video.style.display = 'none';
        }
      }
    });
  }

  /* ==========================================
     3. STICKY ADD-TO-CART BAR
     Automatically appears on product pages
     when the main ATC button scrolls out of view.
     No Liquid changes needed — pure JS.
     ========================================== */

  function initStickyATC() {
    // Only run on product pages
    if (!document.body.classList.contains('template-product')) return;

    const productForm = document.querySelector('.product-form');
    const mainATCBtn  = document.querySelector('.product-form__submit');
    if (!productForm || !mainATCBtn) return;

    // Get product info for the bar
    const productTitle = document.querySelector('.product__title');
    const productPrice = document.querySelector('.product__price .price');

    // Build the sticky bar
    const bar = document.createElement('div');
    bar.className = 'sticky-atc';
    bar.setAttribute('aria-label', 'Quick add to cart');
    bar.innerHTML = `
      <div class="sticky-atc__info">
        <span class="sticky-atc__title">${productTitle ? productTitle.textContent.trim() : 'SUPERBAND'}</span>
        <span class="sticky-atc__price">${productPrice ? productPrice.textContent.trim() : ''}</span>
      </div>
      <div class="sticky-atc__variants" id="sticky-variants"></div>
      <button class="sticky-atc__btn" id="sticky-atc-btn">Add to Cart</button>
    `;
    document.body.appendChild(bar);

    // Mirror variant selection into sticky bar
    const variantSelects = document.querySelectorAll('.variant-pills__input, .product-form select');
    const stickyVariants = bar.querySelector('#sticky-variants');
    const stickyBtn     = bar.querySelector('#sticky-atc-btn');

    // Clone variant pills if they exist
    if (variantSelects.length && stickyVariants) {
      variantSelects.forEach((input) => {
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (label) {
          const pill = document.createElement('button');
          pill.className = 'sticky-atc__variant-pill' + (input.checked ? ' active' : '');
          pill.textContent = label.textContent.trim();
          pill.setAttribute('data-value', input.value);
          pill.addEventListener('click', () => {
            input.click();
            stickyVariants.querySelectorAll('.sticky-atc__variant-pill')
              .forEach(p => p.classList.remove('active'));
            pill.classList.add('active');
          });
          stickyVariants.appendChild(pill);
        }
      });
    }

    // Wire sticky button to submit main form
    stickyBtn.addEventListener('click', () => {
      const mainBtn = document.querySelector('.product-form__submit');
      if (mainBtn) mainBtn.click();
    });

    // Show/hide bar based on main ATC visibility
    const atcObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            bar.classList.add('is-visible');
          } else {
            bar.classList.remove('is-visible');
          }
        });
      },
      { threshold: 0 }
    );

    atcObserver.observe(mainATCBtn);
  }

  /* ==========================================
     4. MEGA MENU
     Enhances existing Dawn header nav.
     To activate: in Customizer, the nav items that
     should have a mega menu need a data-mega attribute.

     In theme.liquid, find the nav link loop and add:
     data-mega="{{ link.title | handleize }}"

     Then define mega menu content in snippets/mega-menu.liquid
     (see template below in this guide)
     ========================================== */

  function initMegaMenu() {
    const menuItems = document.querySelectorAll('.header__menu-item[data-mega]');
    if (!menuItems.length) return;

    menuItems.forEach((item) => {
      const megaId = item.getAttribute('data-mega');
      const panel  = document.querySelector(`.mega-menu[data-panel="${megaId}"]`);
      if (!panel) return;

      let closeTimeout;

      const open = () => {
        clearTimeout(closeTimeout);
        panel.style.display = 'grid';
        // Force reflow before adding class for transition
        panel.getBoundingClientRect();
        panel.classList.add('is-open');
      };

      const close = () => {
        closeTimeout = setTimeout(() => {
          panel.classList.remove('is-open');
          setTimeout(() => { panel.style.display = 'none'; }, 250);
        }, 120);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimeout));
      panel.addEventListener('mouseleave', close);

      // Keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          panel.style.display === 'grid' ? close() : open();
        }
        if (e.key === 'Escape') close();
      });
    });
  }

  /* ==========================================
     APPLY DATA-ANIMATE TO DAWN SECTIONS
     Automatically adds scroll animation attributes
     to standard Dawn sections so you don't need to
     manually touch every section in the Customizer.
     ========================================== */

  function autoTagSections() {
    const sectionMap = [
      { selector: '.banner',                    animation: 'fade-in'    },
      { selector: '.featured-collection',       animation: 'fade-up'    },
      { selector: '.rich-text',                 animation: 'fade-up'    },
      { selector: '.image-with-text',           animation: 'fade-up'    },
      { selector: '.multicolumn',               animation: 'fade-up'    },
      { selector: '.featured-collection .grid', animation: 'fade-up',   group: true },
      { selector: '.multicolumn .columns',      animation: 'fade-up',   group: true },
      { selector: '.card-wrapper',              animation: 'scale-up'   },
      { selector: '.footer',                    animation: 'fade-in'    },
    ];

    sectionMap.forEach(({ selector, animation, group }) => {
      document.querySelectorAll(selector).forEach((el) => {
        // Don't double-tag
        if (el.hasAttribute('data-animate') || el.hasAttribute('data-animate-group')) return;
        if (group) {
          el.setAttribute('data-animate-group', '');
        } else {
          el.setAttribute('data-animate', animation);
        }
      });
    });
  }

  /* ==========================================
     INIT ALL
     ========================================== */

  function init() {
    autoTagSections();
    initScrollAnimations();
    initVideoHero();
    initStickyATC();
    initMegaMenu();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run on Shopify section renders (Customizer live updates)
  document.addEventListener('shopify:section:load', () => {
    autoTagSections();
    initScrollAnimations();
    initVideoHero();
    initStickyATC();
  });

})();